import { isAdminV2LoginPopupSceneSlug } from '@/site-runtime/code-owned/login-popup-scenes';
import { isAdminV2PaywallPopupBranchSlug } from '@/site-runtime/code-owned/paywall-branches';
import { siteRuntimeCodeOwnedPopupIds } from '@/site-runtime/code-owned/popup-ids';
import { z } from 'zod';
import { adminV2ExactIdentitySchema } from '../foundation/admin-v2-identity';

export const adminV2EntryActionTypes = [
  'switch_to_workspace',
  'open_popup',
  'open_checkout',
  'open_url',
  'scroll_to_top',
  'scroll_to_anchor',
] as const;

export const adminV2EntryActionTypeSchema = z.enum(adminV2EntryActionTypes);

const adminV2InternalEntryActionHrefSchema = z
  .string()
  .regex(
    /^\/(?!\/)[\x21-\x5b\x5d-\x7e]*$/,
    'Internal Entry Action URLs must be a safe single-slash path.'
  );

const ADMIN_V2_EXTERNAL_ENTRY_ACTION_HREF_PATTERN =
  /^https?:\/\/(?:(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)*[A-Za-z](?:[A-Za-z0-9-]*[A-Za-z0-9])?|(?:(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(?:25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9]))(?:[/?#][\x21-\x5b\x5d-\x7e]*)?$/i;

const adminV2ExternalEntryActionHrefSchema = z
  .string()
  .regex(
    ADMIN_V2_EXTERNAL_ENTRY_ACTION_HREF_PATTERN,
    'External Entry Action URLs must use the supported HTTP or HTTPS format.'
  );

export const adminV2EntryActionHrefSchema = z.union([
  adminV2InternalEntryActionHrefSchema,
  adminV2ExternalEntryActionHrefSchema,
]);

const adminV2OpenPopupEntryActionSchema = z
  .object({
    type: z.literal('open_popup'),
    popupId: z.enum(siteRuntimeCodeOwnedPopupIds),
    branchSlug: adminV2ExactIdentitySchema.optional(),
  })
  .strict();

export const adminV2EntryActionSchema = z
  .discriminatedUnion('type', [
    z
      .object({
        type: z.literal('switch_to_workspace'),
        targetToolId: adminV2ExactIdentitySchema.optional(),
        startupInput: z
          .object({
            autoSubmit: z.boolean().optional(),
            contextMappings: z
              .array(
                z
                  .object({
                    source: z.enum([
                      'currentPrimaryInput',
                      'currentPrimaryResult',
                    ]),
                    targetInputRole: z.literal('primaryImage'),
                  })
                  .strict()
              )
              .max(1)
              .optional(),
          })
          .strict(),
      })
      .strict(),
    adminV2OpenPopupEntryActionSchema,
    z
      .object({
        type: z.literal('open_checkout'),
        checkoutIntentKey: adminV2ExactIdentitySchema,
      })
      .strict(),
    z
      .object({
        type: z.literal('open_url'),
        href: adminV2EntryActionHrefSchema,
      })
      .strict(),
    z.object({ type: z.literal('scroll_to_top') }).strict(),
    z
      .object({
        type: z.literal('scroll_to_anchor'),
        anchorId: adminV2ExactIdentitySchema,
      })
      .strict(),
  ])
  .superRefine((action, context) => {
    if (action.type !== 'open_popup') {
      return;
    }

    if (action.popupId === 'paywall' && !action.branchSlug) {
      context.addIssue({
        code: 'custom',
        message: 'Paywall popup actions require a branch slug.',
        path: ['branchSlug'],
      });
    }
    if (
      action.popupId === 'paywall' &&
      action.branchSlug &&
      !isAdminV2PaywallPopupBranchSlug(action.branchSlug)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Paywall popup branch is not code-owned.',
        path: ['branchSlug'],
      });
    }
    if (
      action.popupId === 'login' &&
      action.branchSlug &&
      !isAdminV2LoginPopupSceneSlug(action.branchSlug)
    ) {
      context.addIssue({
        code: 'custom',
        message: 'Login popup scene is not code-owned.',
        path: ['branchSlug'],
      });
    }
  });

export type AdminV2EntryAction = z.infer<typeof adminV2EntryActionSchema>;

export function normalizeLegacyAdminV2RuntimeEntryActions(input: unknown) {
  const convertedPaths: Array<Array<string | number>> = [];

  function normalize(value: unknown, path: Array<string | number>): unknown {
    if (Array.isArray(value)) {
      return value.map((item, index) => normalize(item, [...path, index]));
    }
    if (!value || typeof value !== 'object') {
      return value;
    }

    const record = value as Record<string, unknown>;
    if (record.type === 'open_login' && Object.keys(record).length === 1) {
      convertedPaths.push([...path, 'type']);
      return { type: 'open_popup', popupId: 'login' };
    }

    return Object.fromEntries(
      Object.entries(record).map(([key, child]) => [
        key,
        normalize(child, [...path, key]),
      ])
    );
  }

  return { convertedPaths, value: normalize(input, []) };
}

export function parseAdminV2EntryAction(input: unknown) {
  assertEntryActionDoesNotOwnRuntimeTruth(input);
  return adminV2EntryActionSchema.parse(input);
}

export type AdminV2EntryActionPublishReferenceGuard = {
  assertPopupPublishable: (
    popupId: string,
    fieldPath?: string
  ) => Promise<void>;
  assertCheckoutIntentPublishable: (
    checkoutIntentKey: string,
    fieldPath?: string
  ) => Promise<void>;
};

export async function assertAdminV2EntryActionPublishReferences(params: {
  action: AdminV2EntryAction;
  fieldPath?: string;
  referenceGuard: AdminV2EntryActionPublishReferenceGuard;
}) {
  const fieldPath = params.fieldPath ?? 'action';

  if (params.action.type === 'open_popup') {
    await params.referenceGuard.assertPopupPublishable(
      params.action.popupId,
      `${fieldPath}.popupId`
    );
  }

  if (params.action.type === 'open_checkout') {
    await params.referenceGuard.assertCheckoutIntentPublishable(
      params.action.checkoutIntentKey,
      `${fieldPath}.checkoutIntentKey`
    );
  }
}

export function assertEntryActionDoesNotOwnRuntimeTruth(action: unknown) {
  const serialized = JSON.stringify(action);
  for (const forbiddenKey of [
    'modelId',
    'provider',
    'providerParams',
    ['fall', 'back'].join(''),
    'creditCost',
    'priceId',
    'planId',
    'providerSecret',
  ]) {
    if (serialized.includes(`"${forbiddenKey}"`)) {
      throw new Error(`Entry Action cannot own ${forbiddenKey}.`);
    }
  }
}
